import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isAddress, type Address } from "viem";
import { useChainStore } from "@/stores/chain";
import { api } from "@/lib/api";
import { byId } from "@/lib/chains";
import { ExplorerLink } from "@/components/ExplorerLink";
import {
  KindFilter,
  KIND_FILTER_VALUES,
  matchesKindFilter,
  type KindFilterValue,
} from "@/components/KindFilter";
import { CollectionTile, type CollectionTileData } from "@/components/CollectionTile";

type Collection = CollectionTileData & {
  description?: string;
  bannerCid?: string;
  createdAt: number;
};

export function Creator() {
  const { address: addrParam } = useParams<{ address: string }>();
  const activeChainId = useChainStore((s) => s.activeChainId);
  const chain = byId(activeChainId);
  const address = (addrParam ?? "").toLowerCase() as Address;
  const valid = isAddress(address);

  const [filter, setFilter] = useState<KindFilterValue>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["creator-collections", activeChainId, address],
    queryFn: () =>
      api.get<{ items: Collection[] }>(
        `/api/collections?chain=${activeChainId}&creator=${address}&limit=100`,
      ),
    enabled: valid,
  });
  const all = useMemo(() => data?.items ?? [], [data]);
  const ipfsGateway = chain?.ipfsGateway ?? "https://dweb.link/ipfs/";

  const counts = useMemo(() => {
    const out: Partial<Record<KindFilterValue, number>> = {};
    for (const v of KIND_FILTER_VALUES) {
      out[v] = all.filter((c) => matchesKindFilter(c, v)).length;
    }
    return out;
  }, [all]);

  const items = useMemo(() => all.filter((c) => matchesKindFilter(c, filter)), [all, filter]);

  if (!valid) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <h1 className="mb-2 text-xl font-semibold">Creator not found</h1>
        <p className="text-sm text-muted-foreground">
          <code>/{addrParam}</code> isn't a valid wallet address.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-6">
        <p className="mb-1 text-xs uppercase tracking-wide text-muted-foreground">Creator</p>
        <h1 className="mb-2 break-all font-mono text-xl">
          {address.slice(0, 10)}…{address.slice(-8)}
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{chain?.name ?? "—"}</span>
          {chain && (
            <>
              <span>·</span>
              <ExplorerLink chain={chain} kind="address" value={address} className="text-xs" />
            </>
          )}
          <span>·</span>
          <span>
            {all.length} collection{all.length === 1 ? "" : "s"}
          </span>
        </div>
      </header>

      <div className="mb-4">
        <KindFilter value={filter} onChange={setFilter} counts={counts} />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="aspect-square animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : all.length === 0 ? (
        <div className="card text-center text-sm text-muted-foreground">
          This wallet hasn't deployed any collections on {chain?.name ?? "this chain"} yet.
          <div className="mt-3">
            <Link to="/" className="btn-secondary text-xs">Back to home</Link>
          </div>
        </div>
      ) : items.length === 0 ? (
        <div className="card text-center text-sm text-muted-foreground">
          No collections match this filter.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {items.map((c, i) => (
            <CollectionTile
              key={c.id}
              c={c}
              shortName={chain?.shortName ?? `c${activeChainId}`}
              ipfsGateway={ipfsGateway}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}
